import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, GripVertical, Handshake, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loading } from "@/components/common/Loading";
import { PartnerIcon, PARTNER_ICON_NAMES } from "@/components/partner/partnerIcons";
import { SandboxedEmbed, EMBED_TRUST_WARNING } from "@/components/common/SandboxedEmbed";

const F = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    {label && <Label className="text-sm">{label}</Label>}
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

function StringList({ items, onChange, placeholder, testId }) {
  const list = items || [];
  const set = (i, v) => onChange(list.map((x, idx) => (idx === i ? v : x)));
  const add = () => onChange([...list, ""]);
  const del = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid={testId}>
      {list.map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/50" />
          <Input value={v} onChange={(e) => set(i, e.target.value)} placeholder={placeholder} data-testid={`${testId}-input-${i}`} />
          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-destructive" onClick={() => del(i)} data-testid={`${testId}-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button size="sm" variant="secondary" onClick={add} data-testid={`${testId}-add`}><Plus className="mr-1 h-3.5 w-3.5" /> Add</Button>
    </div>
  );
}

export default function PartnerEditor() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/partner");
      setC(data);
    } catch {
      toast.error("Could not load partner content.");
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const setOpp = (i, patch) => set("opportunities", c.opportunities.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));
  const setLogo = (i, patch) => set("partner_logos", c.partner_logos.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/partner", c);
      setC(data);
      toast.success("Partner page saved — live on /partner.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  if (!c) return <Loading />;

  return (
    <div data-testid="partner-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-[680] text-foreground"><Handshake className="h-5 w-5 text-primary" /> Partner page content</h2>
          <p className="text-sm text-muted-foreground">Edit every section of <span className="font-[560]">/partner</span> — changes go live instantly, no redeploy.</p>
        </div>
        <a href="/partner" target="_blank" rel="noreferrer"><Button variant="secondary" data-testid="partner-view-live">View live page</Button></a>
      </div>

      <Accordion type="multiple" defaultValue={["hero", "form"]} className="mt-6 space-y-3">
        <AccordionItem value="hero" className="rounded-none border border-border px-4">
          <AccordionTrigger data-testid="partner-acc-hero">Hero section</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <F label="Headline"><Input value={c.hero_headline || ""} onChange={(e) => set("hero_headline", e.target.value)} data-testid="partner-hero-headline-input" /></F>
            <F label="Subheadline"><Textarea rows={3} value={c.hero_subheadline || ""} onChange={(e) => set("hero_subheadline", e.target.value)} data-testid="partner-hero-subheadline-input" /></F>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <F label="Primary button label"><Input value={c.hero_cta_label || ""} onChange={(e) => set("hero_cta_label", e.target.value)} /></F>
              <F label="Primary button target" hint="#partner-form scrolls to the form"><Input value={c.hero_cta_target || ""} onChange={(e) => set("hero_cta_target", e.target.value)} /></F>
              <F label="Secondary button label"><Input value={c.hero_secondary_label || ""} onChange={(e) => set("hero_secondary_label", e.target.value)} /></F>
              <F label="Secondary button target" hint="#ways-to-partner scrolls to opportunities"><Input value={c.hero_secondary_target || ""} onChange={(e) => set("hero_secondary_target", e.target.value)} /></F>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="why" className="rounded-none border border-border px-4">
          <AccordionTrigger data-testid="partner-acc-why">Why partner</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <F label="Heading"><Input value={c.why_heading || ""} onChange={(e) => set("why_heading", e.target.value)} /></F>
            <F label="Body" hint="Separate paragraphs with a blank line"><Textarea rows={5} value={c.why_body || ""} onChange={(e) => set("why_body", e.target.value)} /></F>
            <F label="Highlight points"><StringList items={c.why_points} onChange={(v) => set("why_points", v)} placeholder="Highlight point" testId="partner-why-points" /></F>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="who" className="rounded-none border border-border px-4">
          <AccordionTrigger data-testid="partner-acc-who">Who we partner with</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <F label="Heading"><Input value={c.who_heading || ""} onChange={(e) => set("who_heading", e.target.value)} /></F>
            <F label="Intro"><Textarea rows={2} value={c.who_intro || ""} onChange={(e) => set("who_intro", e.target.value)} /></F>
            <F label="Partner types"><StringList items={c.who_types} onChange={(v) => set("who_types", v)} placeholder="Partner type" testId="partner-who-types" /></F>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="opps" className="rounded-none border border-border px-4">
          <AccordionTrigger data-testid="partner-acc-opps">Ways to partner</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <F label="Heading"><Input value={c.opportunities_heading || ""} onChange={(e) => set("opportunities_heading", e.target.value)} /></F>
            <div className="space-y-3">
              {(c.opportunities || []).map((o, i) => (
                <Card key={i} className="space-y-3 p-4" data-testid={`partner-opp-editor-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-[640] text-muted-foreground">Opportunity {i + 1}</span>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => set("opportunities", c.opportunities.filter((_, idx) => idx !== i))} data-testid={`partner-opp-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="sm:col-span-2"><Input value={o.title || ""} onChange={(e) => setOpp(i, { title: e.target.value })} placeholder="Title" data-testid={`partner-opp-title-${i}`} /></div>
                    <Select value={o.icon || "Handshake"} onValueChange={(v) => setOpp(i, { icon: v })}>
                      <SelectTrigger data-testid={`partner-opp-icon-${i}`}><span className="flex items-center gap-2"><PartnerIcon name={o.icon} className="h-4 w-4" /><SelectValue /></span></SelectTrigger>
                      <SelectContent className="max-h-72">{PARTNER_ICON_NAMES.map((n) => <SelectItem key={n} value={n}><span className="flex items-center gap-2"><PartnerIcon name={n} className="h-4 w-4" />{n}</span></SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Textarea rows={2} value={o.body || ""} onChange={(e) => setOpp(i, { body: e.target.value })} placeholder="Description" data-testid={`partner-opp-body-${i}`} />
                </Card>
              ))}
              <Button size="sm" variant="secondary" onClick={() => set("opportunities", [...(c.opportunities || []), { title: "", body: "", icon: "Handshake" }])} data-testid="partner-opp-add"><Plus className="mr-1 h-3.5 w-3.5" /> Add opportunity</Button>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="benefits" className="rounded-none border border-border px-4">
          <AccordionTrigger data-testid="partner-acc-benefits">Partnership benefits</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <F label="Heading"><Input value={c.benefits_heading || ""} onChange={(e) => set("benefits_heading", e.target.value)} /></F>
            <F label="Intro"><Textarea rows={2} value={c.benefits_intro || ""} onChange={(e) => set("benefits_intro", e.target.value)} /></F>
            <F label="Benefits"><StringList items={c.benefits} onChange={(v) => set("benefits", v)} placeholder="Benefit" testId="partner-benefits-list" /></F>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="matters" className="rounded-none border border-border px-4">
          <AccordionTrigger data-testid="partner-acc-matters">Why it matters</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <F label="Heading"><Input value={c.why_matters_heading || ""} onChange={(e) => set("why_matters_heading", e.target.value)} /></F>
            <F label="Body" hint="Separate paragraphs with a blank line"><Textarea rows={5} value={c.why_matters_body || ""} onChange={(e) => set("why_matters_body", e.target.value)} /></F>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="form" className="rounded-none border border-border px-4">
          <AccordionTrigger data-testid="partner-acc-form">Form embed</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <F label="Heading"><Input value={c.form_heading || ""} onChange={(e) => set("form_heading", e.target.value)} /></F>
            <F label="Intro"><Textarea rows={3} value={c.form_intro || ""} onChange={(e) => set("form_intro", e.target.value)} /></F>
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="partner-embed-warning">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{EMBED_TRUST_WARNING}</span>
            </div>
            <F label="Form embed code (HTML)" hint="Paste your Cognito Forms (or other) embed code — seamless/script or iframe. It renders inside a sandboxed frame on /partner with no redeploy. Leave blank to show a placeholder.">
              <Textarea rows={8} value={c.form_embed_html || ""} onChange={(e) => set("form_embed_html", e.target.value)} className="font-mono text-xs" placeholder="<!-- Paste your Cognito Forms embed code here -->" data-testid="partner-form-embed-input" />
            </F>
            {c.form_embed_html && c.form_embed_html.trim() ? (
              <div className="space-y-1.5" data-testid="partner-embed-preview">
                <Label className="text-sm">Preview (sandboxed)</Label>
                <div className="rounded-md border border-border bg-background p-2">
                  <SandboxedEmbed html={c.form_embed_html} title="Partner form preview" minHeight={420} />
                </div>
              </div>
            ) : null}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="logos" className="rounded-none border border-border px-4">
          <AccordionTrigger data-testid="partner-acc-logos">Partner logos</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <F label="Heading"><Input value={c.partners_heading || ""} onChange={(e) => set("partners_heading", e.target.value)} /></F>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!c.partners_coming_soon} onCheckedChange={(v) => set("partners_coming_soon", v)} data-testid="partner-coming-soon-toggle" /> Show &ldquo;Coming soon&rdquo; (hide logos)</label>
            <F label="Coming-soon text"><Input value={c.partners_coming_soon_text || ""} onChange={(e) => set("partners_coming_soon_text", e.target.value)} /></F>
            <Separator />
            <div className="space-y-3">
              {(c.partner_logos || []).map((l, i) => (
                <Card key={i} className="space-y-3 p-4" data-testid={`partner-logo-editor-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-[640] text-muted-foreground">Logo {i + 1}</span>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => set("partner_logos", c.partner_logos.filter((_, idx) => idx !== i))} data-testid={`partner-logo-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Input value={l.name || ""} onChange={(e) => setLogo(i, { name: e.target.value })} placeholder="Partner name" />
                    <Input value={l.label || ""} onChange={(e) => setLogo(i, { label: e.target.value })} placeholder="Label (e.g. Event Partner)" />
                    <Input value={l.logo_url || ""} onChange={(e) => setLogo(i, { logo_url: e.target.value })} placeholder="Logo image URL" />
                    <Input value={l.url || ""} onChange={(e) => setLogo(i, { url: e.target.value })} placeholder="Website URL" />
                  </div>
                </Card>
              ))}
              <Button size="sm" variant="secondary" onClick={() => set("partner_logos", [...(c.partner_logos || []), { name: "", label: "", logo_url: "", url: "" }])} data-testid="partner-logo-add"><Plus className="mr-1 h-3.5 w-3.5" /> Add logo</Button>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="final" className="rounded-none border border-border px-4">
          <AccordionTrigger data-testid="partner-acc-final">Final call-to-action</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <F label="Heading"><Input value={c.final_heading || ""} onChange={(e) => set("final_heading", e.target.value)} /></F>
            <F label="Body" hint="Separate paragraphs with a blank line"><Textarea rows={4} value={c.final_body || ""} onChange={(e) => set("final_body", e.target.value)} /></F>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <F label="Button label"><Input value={c.final_cta_label || ""} onChange={(e) => set("final_cta_label", e.target.value)} /></F>
              <F label="Button target"><Input value={c.final_cta_target || ""} onChange={(e) => set("final_cta_target", e.target.value)} /></F>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="sticky bottom-4 mt-6 flex justify-end">
        <Button size="lg" onClick={save} disabled={saving} className="shadow-lg" data-testid="partner-save-button"><Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save changes"}</Button>
      </div>
    </div>
  );
}
